"use client"

import { motion } from "framer-motion"
import { Zap, Heart, Brain, Frown, Smile, ThumbsUp, AlertCircle, TrendingUp } from "lucide-react"
import { useState } from "react"

export default function ScenariosView({ onSelectScenario }) {
  const [selectedCategory, setSelectedCategory] = useState("all")

  const scenarios = [
    {
      id: "happy-sad",
      title: "Happy to Sad Transition",
      description: "Explore how your brain transitions from happiness to sadness",
      icon: "😊→😢",
      category: "mood",
      color: "#ff006e",
      regions: ["Amygdala", "Prefrontal Cortex", "Limbic System"],
      duration: "2-5 minutes",
      difficulty: "Beginner"
    },
    {
      id: "grief-thoughts",
      title: "Grief to Deep Thoughts",
      description: "Understand the neural pathways from grief to contemplation",
      icon: "😔→🤔",
      category: "mood",
      color: "#7209b7",
      regions: ["Amygdala", "Hippocampus", "Prefrontal Cortex"],
      duration: "3-7 minutes",
      difficulty: "Intermediate"
    },
    {
      id: "stress-calm",
      title: "Stress to Calm",
      description: "Learn how meditation affects brain activity",
      icon: "😰→😌",
      category: "wellness",
      color: "#06ffa5",
      regions: ["Amygdala", "Prefrontal Cortex", "Insula"],
      duration: "5-10 minutes",
      difficulty: "Beginner"
    },
    {
      id: "focus-distraction",
      title: "Focus to Distraction",
      description: "See what happens when attention shifts",
      icon: "🎯→💭",
      category: "cognitive",
      color: "#4cc9f0",
      regions: ["Prefrontal Cortex", "Parietal Lobe", "Anterior Cingulate"],
      duration: "2-4 minutes",
      difficulty: "Beginner"
    },
    {
      id: "anger-peace",
      title: "Anger to Peace",
      description: "Visualize emotional regulation in action",
      icon: "😠→☮️",
      category: "mood",
      color: "#ff006e",
      regions: ["Amygdala", "Prefrontal Cortex", "Hippocampus"],
      duration: "4-8 minutes",
      difficulty: "Intermediate"
    },
    {
      id: "anxiety-confidence",
      title: "Anxiety to Confidence",
      description: "Track the neural shift from worry to assurance",
      icon: "😟→💪",
      category: "wellness",
      color: "#ffd60a",
      regions: ["Amygdala", "Prefrontal Cortex", "Basal Ganglia"],
      duration: "5-10 minutes",
      difficulty: "Advanced"
    },
    {
      id: "sleep-wake",
      title: "Sleep to Wakefulness",
      description: "Explore brain activity during sleep-wake transitions",
      icon: "😴→☀️",
      category: "cognitive",
      color: "#667eea",
      regions: ["Thalamus", "Reticular Formation", "Cortex"],
      duration: "3-6 minutes",
      difficulty: "Intermediate"
    },
    {
      id: "learning-mastery",
      title: "Learning to Mastery",
      description: "Watch neural pathways strengthen with practice",
      icon: "📚→🎓",
      category: "cognitive",
      color: "#4361ee",
      regions: ["Hippocampus", "Prefrontal Cortex", "Cerebellum"],
      duration: "10-15 minutes",
      difficulty: "Advanced"
    }
  ]

  const categories = [
    { id: "all", label: "All Scenarios", icon: Brain },
    { id: "mood", label: "Mood Changes", icon: Heart },
    { id: "cognitive", label: "Cognitive", icon: Zap },
    { id: "wellness", label: "Wellness", icon: ThumbsUp }
  ]

  const filteredScenarios = selectedCategory === "all" 
    ? scenarios 
    : scenarios.filter(s => s.category === selectedCategory)

  return (
    <div className="scenarios-view">
      <motion.div
        className="scenarios-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="scenarios-title">
          <Zap className="title-icon" />
          Brain Activity Scenarios
        </h1>
        <p className="scenarios-subtitle">
          Explore pre-configured brain activity patterns and mood transitions
        </p>
      </motion.div>

      {/* Category Filter */}
      <motion.div
        className="category-filter"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {categories.map((category, index) => {
          const Icon = category.icon
          return (
            <motion.button
              key={category.id}
              className={`category-btn ${selectedCategory === category.id ? "active" : ""}`}
              onClick={() => setSelectedCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Icon size={20} />
              <span>{category.label}</span>
            </motion.button>
          )
        })}
      </motion.div>

      {/* Scenarios Grid */}
      <div className="scenarios-grid">
        {filteredScenarios.map((scenario, index) => (
          <motion.div
            key={scenario.id}
            className="scenario-card glass-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.03, y: -5 }}
            onClick={() => onSelectScenario && onSelectScenario(scenario)}
          >
            <div className="scenario-header-section">
              <div className="scenario-icon" style={{ backgroundColor: `${scenario.color}20` }}>
                <span style={{ fontSize: "2rem" }}>{scenario.icon}</span>
              </div>
              <h3 className="scenario-title">{scenario.title}</h3>
            </div>
            
            <div className="scenario-content">
              <p className="scenario-description">{scenario.description}</p>
              
              <div className="scenario-meta">
                <div className="meta-item">
                  <Brain size={14} />
                  <span>{scenario.regions.length} regions</span>
                </div>
                <div className="meta-item">
                  <AlertCircle size={14} />
                  <span>{scenario.duration}</span>
                </div>
              </div>

              <div className="scenario-tags">
                <span className={`difficulty-tag ${scenario.difficulty.toLowerCase()}`}>
                  {scenario.difficulty}
                </span>
                <span className="category-tag">{scenario.category}</span>
              </div>
            </div>

            <motion.button
              className="scenario-action-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ backgroundColor: scenario.color }}
            >
              <TrendingUp size={18} />
              Explore
            </motion.button>
          </motion.div>
        ))}
      </div>

      {/* Info Banner */}
      <motion.div
        className="info-banner glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <AlertCircle size={24} color="#4cc9f0" />
        <div>
          <h4>How to Use Scenarios</h4>
          <p>Click on any scenario card to analyze the brain activity patterns. Each scenario shows which brain regions are active and how they interact during different mental states.</p>
        </div>
      </motion.div>
    </div>
  )
}
